
import React, { useRef } from "react";
import { BusinessLogo } from "./BusinessLogo";
import { DateTimeDisplay } from "./DateTimeDisplay";
import { VideoBackground } from "./VideoBackground";
import { MarkdownDescription } from "@/components/booking/shared/MarkdownDescription";
import { DynamicTextColor } from "./DynamicTextColor";
import { TimeZoneSelect } from "@/components/booking/time-slots/TimeZoneSelect";
import { cn, getBackgroundType, getContrastTextColor } from "@/lib/utils";
import { useBookingContext } from "@/contexts/BookingContext";
import { Clock, MapPin, Globe, ChevronDown, Video } from "lucide-react";
import { getTimezoneInfo } from "@/lib/timezone/timezone-service";
import { Select, SelectTrigger, SelectContent, SelectItem } from '@/components/ui/select';
import { ENHANCED_TIMEZONE_GROUPS } from '@/lib/timezone/types';
import * as SelectPrimitive from '@radix-ui/react-select';

interface SharedLeftColumnProps {
  logoUrl?: string;
  avatarUrl?: string; // NEW: user's avatar URL
  isMobile?: boolean;
  businessName: string;
  meetingName: string;
  description?: string;
  duration: number;
  timezone: string;
  location?: string; // NEW: meeting location
  logoBorderRadius?: "none" | "sm" | "md" | "lg" | "xl" | "full";
  logoSize?: "sm" | "md" | "lg" | "xl";
  // Legacy support
  isLogoRounded?: boolean;
  customizations?: any;
  selectedDate?: Date | null;
  selectedTime?: string | null;
  organizerTimezone?: string;
  onTimezoneChange?: (timezone: string) => void;
  onBackToDateStep?: () => void; // NEW PROP
  className?: string; // ADDED
  isOptimalSlot?: boolean; // NEW: indicates if the selected time is optimal
}

export const SharedLeftColumn: React.FC<SharedLeftColumnProps> = ({
  logoUrl,
  avatarUrl,
  isMobile = false,
  businessName,
  meetingName,
  description,
  duration,
  timezone,
  location = "Google Meet", // NEW: default to Google Meet
  logoBorderRadius = "none",
  logoSize = "md",
  isLogoRounded = false, // Legacy support
  customizations,
  selectedDate,
  selectedTime,
  organizerTimezone,
  onTimezoneChange,
  onBackToDateStep, // NEW
  className, // ADDED
  isOptimalSlot = false // NEW: default to false
}) => {
  // Get booking context for reschedule mode (optional - only if BookingProvider exists)
  let existingBooking = null;
  let isRescheduleMode = false;
  let isCancelMode = false;
  
  try {
    const bookingContext = useBookingContext();
    existingBooking = bookingContext.existingBooking;
    isRescheduleMode = bookingContext.isRescheduleMode;
    isCancelMode = bookingContext.isCancelMode;
  } catch (error) {
    // BookingProvider not found - this is fine for preview/edit pages
  }
  const leftColumnBg = customizations?.leftColumnBackgroundUrl || customizations?.left_column_background_url;
  // Use default video background if no background is specified
  const defaultBackgroundVideo = 'https://videos.pexels.com/video-files/3163534/3163534-uhd_3840_2160_30fps.mp4';
  const effectiveBackgroundUrl = leftColumnBg || defaultBackgroundVideo;
  const backgroundType = getBackgroundType(effectiveBackgroundUrl);
  
  // Debug logging
  console.log('SharedLeftColumn: Background URL:', effectiveBackgroundUrl?.substring(0, 100) + '...');
  console.log('SharedLeftColumn: Background type:', backgroundType);
  console.log('SharedLeftColumn: Has leftColumnBg:', !!leftColumnBg);
  const backgroundRef = useRef<HTMLDivElement>(null);

  // Get custom text colors from customizations, fallback to white
  const titleColor = customizations?.title_text_color || '#ffffff';
  const descriptionColor = customizations?.description_text_color || '#ffffff';
  const durationColor = customizations?.duration_text_color || '#ffffff';

  // Get timezone abbreviation
  let timezoneAbbr = '';
  if (timezone) {
    try {
      timezoneAbbr = getTimezoneInfo(timezone).abbreviation;
    } catch {
      // Fallback abbreviation logic
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(new Date());
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      timezoneAbbr = tzPart ? tzPart.value : '';
    }
  }

  // Custom trigger for timezone selector without border or arrow
  const TimezoneSelectTrigger = React.forwardRef(
    ({ children, className, ...props }: any, ref) => (
      <SelectPrimitive.Trigger
        ref={ref}
        className={cn(
          'bg-transparent border-none shadow-none p-0 h-auto min-w-0 flex items-center gap-1 focus:ring-0 focus:outline-none rounded-none group cursor-pointer',
          className
        )}
        {...props}
      >
        {children}
      </SelectPrimitive.Trigger>
    )
  );
  TimezoneSelectTrigger.displayName = 'TimezoneSelectTrigger';

  // Function to determine the appropriate location icon based on location text
  const renderLocationIcon = () => {
    const locationLower = location.toLowerCase();
    
    // Check for video conferencing platforms
    if (locationLower.includes('google meet') || locationLower.includes('meet.google.com')) {
      return (
        <img 
          src="/assets/icons/platforms/google-meet-icon.svg" 
          alt="Google Meet"
          className="h-4 w-4"
        />
      );
    }
    
    if (locationLower.includes('zoom')) {
      return <Video className="h-4 w-4" style={{ color: durationColor }} />;
    }
    
    // Add more platform checks here in the future
    // if (locationLower.includes('teams')) {
    //   return <img src="/assets/icons/platforms/teams-icon.svg" alt="Teams" className="h-4 w-4" />;
    // }
    
    // Default to MapPin for physical addresses or unknown locations
    return <MapPin className="h-4 w-4 text-white/60" />;
  };

  const renderBackground = () => {
    if (!effectiveBackgroundUrl) return null;

    if (backgroundType === 'video') {
      return (
        <VideoBackground
          videoUrl={effectiveBackgroundUrl}
          className="w-full h-full object-cover"
        />
      );
    }

    if (backgroundType === 'gradient') {
      return (
        <div
          className="w-full h-full"
          style={{ background: effectiveBackgroundUrl }}
        />
      );
    }

    if (backgroundType === 'color') {
      return (
        <div
          className="w-full h-full"
          style={{ backgroundColor: effectiveBackgroundUrl }}
        />
      );
    }

    // Default to image
    return (
      <div
        className="w-full h-full bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${effectiveBackgroundUrl})` }}
      />
    );
  };

  return (
    <div className={cn("relative bg-gray-50 p-4 md:py-8 md:px-4 lg:p-8 flex flex-col h-full", className)}>
      {/* Background */}
      {effectiveBackgroundUrl && (
        <div className="absolute inset-0" ref={backgroundRef}>
          {renderBackground()}
          {/* Only add overlay for images and videos, not for colors or gradients */}
          {(backgroundType === 'image' || backgroundType === 'video') && (
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20" />
          )}
        </div>
      )}

      <div className="relative z-10 grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 grid-flow-row gap-2 h-full justify-between">
        <div>
          {/* Logo and Business Info */}
          <div className="mb-0 md:mb-2 flex justify-start md:justify-start">
            {(logoUrl || avatarUrl) && <BusinessLogo
              logoUrl={logoUrl}
              avatarUrl={avatarUrl}
              businessName={businessName}
              size={logoSize}
              borderRadius={logoBorderRadius}
              isRounded={isLogoRounded} // Legacy support
              className="mb-0"
            />}
          </div>

          {/* Meeting Details */}
          <div className="mb-0 lg:mb-6 flex flex-col space-y-5">
            {/* Meeting Title */}
            <div>
              <h2 
                className="text-xl md:text-3xl font-bold"
                style={{ 
                  color: titleColor
                }}
              >
                {meetingName}
              </h2>
            </div>

            {/* Description */}
            {description && (
              <div 
                className="flex overflow-scroll" 
                style={{ 
                  color: descriptionColor
                }}
              >
                <MarkdownDescription 
                  content={description} 
                  className="text-sm md:text-base lg:text-lg leading-relaxed"
                  textColor={descriptionColor}
                />
              </div>
            )}

            
          </div>
        </div>

        {/* Selected Date/Time Display */}
        <div className="flex flex-col justify-end md:justify-end md:h-full lg:justify-end">
          {/* Meeting Details Section - Desktop Only */}
          <div className="hidden md:block mb-4">
            <div className="space-y-3">
              {/* Duration */}
              <div className="flex items-center gap-2">
                <Clock 
                  className="h-4 w-4" 
                  style={{ color: durationColor }}
                />
                <span 
                  className="text-md font-medium"
                  style={{ color: durationColor }}
                >
                  {duration} min
                </span>
              </div>

              {/* Location */}
              <div className="flex items-center gap-2">
                {renderLocationIcon()}
                <span 
                  className="text-md font-medium"
                  style={{ color: durationColor }}
                >
                  {location}
                </span>
              </div>

              {/* Timezone */}
              <div className="flex items-center gap-2">
                <Globe 
                  className="h-4 w-4" 
                  style={{ color: durationColor }}
                />
                <Select value={timezone || ''} onValueChange={onTimezoneChange}>
                  <TimezoneSelectTrigger>
                    <span 
                      className="text-md font-medium group-hover:text-white/90 transition-colors"
                      style={{ color: durationColor }}
                    >
                      {timezone ? timezone.replace(/_/g, ' ') : 'UTC'} ({timezoneAbbr || 'UTC'})
                    </span>
                    <ChevronDown className="h-3 w-3 text-white/60 group-hover:text-white/90 transition-colors ml-1" />
                  </TimezoneSelectTrigger>
                  <SelectContent className="max-h-80 min-w-[220px]">
                    {Object.entries(ENHANCED_TIMEZONE_GROUPS).map(([groupName, timezones]) => (
                      <div key={groupName}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50">
                          {groupName}
                        </div>
                        {timezones.map((tz) => {
                          const info = getTimezoneInfo(tz);
                          return (
                            <SelectItem key={tz} value={tz}>
                              <div className="flex basis-full items-center justify-between w-full">
                                <span className="flex-1">
                                  {tz.replace(/_/g, ' ')} ({info.abbreviation})
                                </span>
                                <span className="text-md text-gray-500 ml-4 flex items-center gap-1">
                                  <Clock className="h-3 w-3" style={{ color: durationColor }} />
                                  {info.currentTime}
                                </span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DateTimeDisplay
            selectedDate={
              // In reschedule/cancel mode: show new selection if available, otherwise show existing booking
              (isRescheduleMode || isCancelMode) && existingBooking 
                ? (selectedDate || new Date(existingBooking.booking_date))
                : selectedDate
            }
            selectedTime={
              // In reschedule/cancel mode: show new selection if available, otherwise show existing booking time
              (isRescheduleMode || isCancelMode) && existingBooking 
                ? (selectedTime || new Date(existingBooking.booking_date).toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    hour12: false 
                  }))
                : selectedTime
            }
            duration={duration}
            timezone={timezone}
            organizerTimezone={organizerTimezone}
            onBackToDateStep={onBackToDateStep}
            onTimezoneChange={onTimezoneChange}
            isOptimalSlot={isOptimalSlot}
          />
          {/* TimeZoneSelect is now only rendered inside DateTimeDisplay popover */}
        </div>
      </div>
    </div>
  );
};
